import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Markdown } from "@/lib/markdown";
import { LockedFeature } from "@/components/LockedFeature";
import { toast } from "sonner";
import { FileSignature, Loader2, AlertTriangle, ListChecks, HelpCircle, Tag } from "lucide-react";

export default function DealBuilder() {
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.tier === "pro";
  const [types, setTypes] = useState([]);
  const [type, setType] = useState("");
  const [context, setContext] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/deal-builder/types").then((r) => { setTypes(r.data); setType(r.data[0]); }).catch(() => {}); }, []);

  if (!isPro) return (
    <LockedFeature title="Deal Builder" description="Generate complete deal structures, negotiation checklists, key terms, red flags and the exact questions to ask — for any kind of deal."
      perks={["Startup, Agency, SaaS, Real Estate & more", "Negotiation checklists & red flags", "Term-by-term explanations"]} />
  );

  const build = async () => {
    setLoading(true); setResult(null);
    try {
      const { data } = await api.post("/deal-builder", { deal_type: type, context });
      setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to build deal"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-8" data-testid="deal-builder-page">
      <div>
        <span className="font-mono-eco text-xs uppercase tracking-widest text-slate-500">Deal Builder</span>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-white tracking-tight mt-1">Walk into any deal prepared.</h1>
        <p className="text-slate-400 mt-2">Pick a deal type and ECO builds your structure, checklist, key terms, red flags and questions to ask.</p>
      </div>

      <div className="eco-card p-6 space-y-4">
        <div>
          <div className="font-mono-eco text-[10px] uppercase tracking-widest text-slate-500 mb-2">Deal type</div>
          <div className="flex flex-wrap gap-2">
            {types.map((t) => (
              <button key={t} data-testid={`deal-type-${t}`} onClick={() => setType(t)} className={`text-sm font-medium px-4 py-2 rounded-full border transition-all ${type === t ? "bg-white text-black border-white" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>{t}</button>
            ))}
          </div>
        </div>
        <textarea data-testid="deal-context" value={context} onChange={(e) => setContext(e.target.value)} rows={3} placeholder="Optional: add context (e.g., 'raising $500k pre-seed for an AI app')…" className="eco-input resize-none" />
        <button data-testid="deal-build-btn" onClick={build} disabled={loading} className="btn-accent">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <><FileSignature size={16} /> Build deal guide</>}
        </button>
      </div>

      {result && (
        <div className="space-y-5" data-testid="deal-result">
          <div className="eco-card p-6">
            <h3 className="font-display text-lg font-semibold text-white mb-3">{result.deal_type} — Deal structure</h3>
            <Markdown>{result.deal_structure}</Markdown>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <Block icon={ListChecks} title="Negotiation checklist" items={result.negotiation_checklist} color="#10B981" />
            <Block icon={AlertTriangle} title="Red flags" items={result.red_flags} color="#EF4444" />
          </div>
          {result.important_terms?.length > 0 && (
            <div className="eco-card p-6">
              <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-white mb-4"><Tag size={18} className="text-[#4d8bff]" /> Important terms</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                {result.important_terms.map((t, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="font-medium text-white">{t.term}</div>
                    <div className="text-sm text-slate-400 mt-1">{t.explanation}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Block icon={HelpCircle} title="Questions to ask" items={result.questions_to_ask} color="#A855F7" full />
        </div>
      )}
    </div>
  );
}

function Block({ icon: I, title, items, color, full }) {
  if (!items?.length) return null;
  return (
    <div className={`eco-card p-6 ${full ? "" : ""}`}>
      <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-white mb-4"><I size={18} style={{ color }} /> {title}</h3>
      <ul className="space-y-2.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-slate-300">
            <span className="h-1.5 w-1.5 rounded-full mt-2 shrink-0" style={{ background: color }} /> {it}
          </li>
        ))}
      </ul>
    </div>
  );
}
